import { cluesFromGrid } from '../clues.js';
import { rateDifficulty } from '../difficulty.js';
import { analyzeSolutions, solve } from '../solver/index.js';
import type {
  DifficultyRating,
  GenerateOptions,
  GenerateResult,
  Puzzle,
  ProvenUniqueSolutionAnalysis,
} from '../types.js';
import { adaptPuzzle } from './adapt.js';
import { mulberry32 } from './prng.js';
import { resolveSeed } from './seed.js';

const DIFFICULTY_ORDER: DifficultyRating[] = [
  'trivial',
  'easy',
  'medium',
  'hard',
  'expert',
];

/**
 * Generate a random nonogram puzzle with a guaranteed unique solution.
 *
 * When `constructive` is true, uses the Adapt algorithm to guarantee
 * a line-solvable puzzle on the first attempt. Otherwise, tries random
 * grids until one passes uniqueness + difficulty filters.
 */
export function generatePuzzle(options: GenerateOptions): GenerateResult {
  if (options.constructive) {
    return generateConstructive(options);
  }
  return generateFiltered(options);
}

/**
 * Constructive generation: produce a random grid, then use adaptPuzzle
 * to make it line-solvable. Always succeeds on the first attempt.
 */
function generateConstructive(options: GenerateOptions): GenerateResult {
  const { rows, cols, density: targetDensity, seed: initialSeed } = options;

  const seed = resolveSeed(initialSeed);
  const rand = mulberry32(seed);
  const density = targetDensity ?? 0.4 + rand() * 0.25;
  const grid = generateGrid(rows, cols, density, rand);
  const clues = cluesFromGrid(grid);

  const adapted = adaptPuzzle(grid, clues);

  const puzzle: Puzzle = {
    clues: adapted.clues,
    rows,
    cols,
    metadata: { name: `${rows}x${cols} #${seed % 10000}` },
  };

  const solveResult = solve(puzzle);
  const difficulty: DifficultyRating = rateDifficulty(solveResult);

  const uniquenessProof: ProvenUniqueSolutionAnalysis = {
    count: 1,
    complete: true,
    aborted: false,
    exploredStates: 0,
  };

  return {
    puzzle,
    solution: adapted.grid,
    seed,
    difficulty,
    attempts: 1,
    uniqueness: uniquenessProof,
  };
}

/**
 * Filter-based generation: tries random grids until one produces a puzzle
 * that has a unique solution and meets the difficulty constraint.
 */
function generateFiltered(options: GenerateOptions): GenerateResult {
  const {
    rows,
    cols,
    density: targetDensity,
    seed: initialSeed,
    maxDifficulty = 'expert',
    maxAttempts = 100,
    uniquenessMaxStates,
  } = options;

  const seed = resolveSeed(initialSeed);
  const rand = mulberry32(seed);

  const maxDiffIdx = DIFFICULTY_ORDER.indexOf(maxDifficulty);

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const density = targetDensity ?? 0.4 + rand() * 0.25;
    const grid = generateGrid(rows, cols, density, rand);
    const clues = cluesFromGrid(grid);
    const puzzle: Puzzle = { clues, rows, cols };
    const uniqueness = analyzeSolutions(puzzle, {
      limit: 2,
      maxStates: uniquenessMaxStates,
    });
    if (!uniqueness.complete || uniqueness.count !== 1) continue;

    const solveResult = solve(puzzle);
    const difficulty: DifficultyRating = rateDifficulty(solveResult);
    const diffIdx = DIFFICULTY_ORDER.indexOf(difficulty);

    if (diffIdx <= maxDiffIdx) {
      const uniquenessProof: ProvenUniqueSolutionAnalysis = {
        ...uniqueness,
        count: 1,
        complete: true,
        aborted: false,
      };
      return {
        puzzle: {
          ...puzzle,
          metadata: { name: `${rows}x${cols} #${seed % 10000}` },
        },
        solution: grid,
        seed,
        difficulty,
        attempts: attempt,
        uniqueness: uniquenessProof,
      };
    }
  }

  throw new Error(
    `Failed to generate a uniqueness-proven ${rows}x${cols} puzzle within ${maxAttempts} attempts`
  );
}

function generateGrid(
  rows: number,
  cols: number,
  density: number,
  rand: () => number
): boolean[][] {
  const grid: boolean[][] = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => rand() < density)
  );

  // Ensure no all-empty rows
  for (let r = 0; r < rows; r++) {
    if (grid[r].every((c) => !c)) {
      grid[r][Math.floor(rand() * cols)] = true;
    }
  }

  // Ensure no all-empty columns
  for (let c = 0; c < cols; c++) {
    if (grid.every((row) => !row[c])) {
      grid[Math.floor(rand() * rows)][c] = true;
    }
  }

  return grid;
}
